import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Api from "../Api/Api.json";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { parse, format } from "date-fns";
import MainContainer from "../components/Container/MainContainer";
import BottomTab from "./BottomTab";

const TimeConverter = (dateTime24) => {
  const parsedDate = parse(dateTime24, "yyyy-MM-dd HH:mm:ss", new Date());
  const formattedTime = format(parsedDate, "MMMM dd, yyyy, hh:mm:ss a");

  return <div>{formattedTime}</div>;
};

const ReportList = () => {
  // const [prescription_list, setPrescriptionList] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const patient_id = JSON.parse(localStorage.getItem("user_info")).di_id;
  const [reportList, setReportList] = useState([]);

  const fetchData = async () => {
    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.PATIENT_API;
    const requestData = {
      task: "getPatientDocumentsList",
      patient_id: patient_id,
    };

    try {
      const result = await axios.post(url, requestData, headers);
      return result.data;
    } catch (error) {
      return null;
    }
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["getPatientDocumentsList" + patient_id],
    queryFn: fetchData,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });

  const handleRecordClick = async (record, index) => {
    // Toggle accordion
    setExpandedId(expandedId === index ? null : index);

    // Only fetch if not already loaded
    if (!record.htmlString) {
      const headers = { headers: { "Content-Type": "application/json" } };
      const url = Api.PATIENT_API;
      const requestData = {
        task: "getPathologyReportHtmlString",
        patient_id: patient_id,
        test_date: record.test_date,
        test_id: record.test_id,
        table_name: record.table_name,
      };

      try {
        const response = await axios.post(url, requestData, headers);
        if (response.data.length > 0) {
          setReportList(
            reportList.map((item) => {
              if (item.test_id === record.test_id) {
                return {
                  ...item,
                  htmlString: response.data,
                };
              }
              return item;
            })
          );
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    if (data && data.length > 0) {
      const arr = [];
      data[2].forEach((obj) => {
        arr.push({
          table_name: obj["table_name"],
          test_date: obj["document_date"],
          test_id: obj["report_id"],
        });
      });
      setReportList(arr);
    }
  }, [data]);

  const renderHTML = (htmlString) => {
    return { __html: htmlString };
  };

  return (
    <MainContainer
      background="bg-gray-50"
      py="py-6"
      px="px-4"
      className="overflow-y-auto"
    >
      <div className="max-w-4xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : reportList.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <h3 className="text-xl font-semibold text-gray-700">
              No Records Found
            </h3>
            <p className="mt-2 text-gray-500">
              No pathology reports are available for this patient.
            </p>
          </div>
        ) : (
          <div>
            <div className="bg-blue-500 p-4 rounded-t-lg">
              <h3 className="text-xl font-bold text-white text-center">
                Pathology Reports
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {reportList.map((item, index) => (
                <div key={index} className="w-full">
                  <button
                    onClick={() => handleRecordClick(item, index)}
                    className="w-full px-4 py-4 flex items-center justify-between focus:outline-none bg-white hover:bg-gray-100 border-blue-500 rounded-lg shadow-sm mb-2"
                  >
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-blue-700"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                      </div>
                      <div className="text-left text-black">
                        {TimeConverter(item.test_date)}
                      </div>
                    </div>
                    <div
                      className={`transition-transform duration-200 ${
                        expandedId === index ? "rotate-180" : ""
                      }`}
                    >
                      <ChevronDown size={20} className="text-gray-500" />
                    </div>
                  </button>
                  {/* Content panel */}
                  <div
                    className={`transition-all duration-200 overflow-hidden ${
                      expandedId === index ? "max-h-screen py-4" : "max-h-0"
                    }`}
                  >
                    {item.htmlString ? (
                      <div
                        dangerouslySetInnerHTML={renderHTML(item.htmlString)}
                        className="prose max-w-none"
                      />
                    ) : (
                      <div className="flex justify-center items-center h-20">
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default ReportList;
